import { useState, useEffect } from 'react'
import { apiClient, type User, type Organization, type Session } from '../lib/api-client'

interface DashboardState {
  loading: boolean
  error: string | null
  user: User | null
  organization: Organization | null
  activeSession: Session | null
  creatingSession: boolean
  copied: string | null
}

const Dashboard = (): JSX.Element => {
  const [state, setState] = useState<DashboardState>({
    loading: true,
    error: null,
    user: null,
    organization: null,
    activeSession: null,
    creatingSession: false,
    copied: null
  })

  // Load initial data
  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }))

      // Load user and organization data
      const [userResponse, orgResponse] = await Promise.all([
        apiClient.getCurrentUser(),
        apiClient.getOrganization()
      ])

      if (!userResponse.success) {
        throw new Error(userResponse.error || 'Failed to load user data')
      }

      if (!orgResponse.success) {
        throw new Error(orgResponse.error || 'Failed to load organization data')
      }

      // Check for active sessions
      const sessionsResponse = await apiClient.getSessions({ status: 'waiting' })
      const activeSession = sessionsResponse.success && sessionsResponse.data && sessionsResponse.data.sessions.length > 0 
        ? sessionsResponse.data.sessions[0] 
        : null

      setState({
        loading: false,
        error: null,
        user: userResponse.data!,
        organization: orgResponse.data!,
        activeSession,
        creatingSession: false,
        copied: null
      })
    } catch (error) {
      console.error('Failed to load dashboard data:', error)
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load dashboard data'
      }))
    }
  }

  const createSession = async (): Promise<void> => {
    setState(prev => ({ ...prev, creatingSession: true, error: null }))
    
    try {
      const response = await apiClient.createSession({
        title: 'Support Session'
      })

      if (!response.success) {
        throw new Error(response.error || 'Failed to create session')
      }

      setState(prev => ({
        ...prev,
        activeSession: response.data!,
        creatingSession: false
      }))
    } catch (error) {
      console.error('Failed to create session:', error)
      setState(prev => ({
        ...prev,
        creatingSession: false,
        error: error instanceof Error ? error.message : 'Failed to create session'
      }))
    }
  }

  const endSession = async (): Promise<void> => {
    if (!state.activeSession) return

    try {
      setState(prev => ({ ...prev, error: null }))
      
      const response = await apiClient.endSession(state.activeSession.id)
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to end session')
      }

      setState(prev => ({ ...prev, activeSession: null }))
    } catch (error) {
      console.error('Failed to end session:', error)
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to end session'
      }))
    }
  }

  const copyToClipboard = async (text: string, type: 'code' | 'link'): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text)
      setState(prev => ({ ...prev, copied: type }))
      setTimeout(() => {
        setState(prev => ({ ...prev, copied: null }))
      }, 2000)
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
    }
  }

  if (state.loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-red-800 mb-2">Error Loading Dashboard</h2>
            <p className="text-red-600 mb-4">{state.error}</p>
            <button
              onClick={loadDashboardData}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!state.organization || !state.user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Failed to load dashboard data</p>
        </div>
      </div>
    )
  }

  const { organization, user, activeSession, copied } = state
  const joinUrl = activeSession ? `${window.location.origin}/join/${activeSession.code}` : ''

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-5">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-semibold text-slate-900">StreamShare</h1>
                <p className="text-xs text-slate-500">{user.firstName} {user.lastName} • {organization.name}</p>
              </div>
            </div>
            <div className="flex items-center space-x-6">
              <div className="text-right">
                <div className="text-xs text-slate-500 uppercase tracking-wider">Plan</div>
                <div className="text-sm font-medium text-slate-900 capitalize">{organization.subscription.plan}</div>
              </div>
              <button className="text-slate-400 hover:text-slate-600 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Error Banner */}
        {state.error && (
          <div className="bg-red-50/50 backdrop-blur-sm border border-red-200 rounded-xl p-4 mb-8 animate-fadeIn">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="ml-3 text-sm text-red-800">{state.error}</p>
              <button 
                onClick={() => setState(prev => ({ ...prev, error: null }))}
                className="ml-auto flex-shrink-0 text-red-400 hover:text-red-500 focus:outline-none"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Session Status */}
        {!activeSession ? (
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="mb-8">
                <div className="w-24 h-24 mx-auto bg-gradient-to-br from-blue-50 to-blue-100 rounded-3xl flex items-center justify-center shadow-inner">
                  <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <h2 className="text-2xl font-semibold text-slate-900 mb-3">No Active Session</h2>
              <p className="text-slate-500 mb-8 max-w-md mx-auto">
                Start a new support session to help your customer with screen sharing
              </p>
              <button
                onClick={createSession}
                disabled={state.creatingSession}
                className="group relative inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transform transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-md"
              >
                <span className="relative flex items-center">
                  {state.creatingSession ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating Session...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Start New Session
                    </>
                  )}
                </span>
              </button>
            </div>
          </div>
        ) : (
          <div className="animate-fadeIn">
            {/* Active Session Card */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="bg-gradient-to-r from-green-500 to-green-600 px-8 py-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                    <span className="text-white font-medium">Session Active</span>
                  </div>
                  <button
                    onClick={endSession}
                    className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-lg hover:bg-white/30 transition-all duration-200 text-sm font-medium"
                  >
                    End Session
                  </button>
                </div>
              </div>
              
              <div className="p-8">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-slate-900 mb-2">Waiting for Customer</h2>
                  <p className="text-slate-500">Share the code below with your customer to start the session</p>
                </div>

                {/* Session Code Display */}
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-8 mb-6">
                  <div className="text-center">
                    <div className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-3">Session Code</div>
                    <div className="text-5xl font-bold text-slate-900 tracking-wider font-mono mb-6">
                      {activeSession.code}
                    </div>
                    <div className="flex items-center justify-center space-x-3">
                      <button
                        onClick={() => copyToClipboard(activeSession.code, 'code')}
                        className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                          copied === 'code' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-white text-slate-700 hover:bg-slate-50 shadow-sm border border-slate-200'
                        }`}
                      >
                        {copied === 'code' ? (
                          <>
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Copied!
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            Copy Code
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => copyToClipboard(joinUrl, 'link')}
                        className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                          copied === 'link' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                        }`}
                      >
                        {copied === 'link' ? (
                          <>
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Copied!
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                            Copy Link
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Instructions */}
                <div className="bg-blue-50 rounded-xl p-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-900">How to connect</h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <ol className="list-decimal list-inside space-y-1">
                          <li>Share the 6-digit code with your customer</li>
                          <li>They'll enter it at the join page</li>
                          <li>Video connection will start automatically</li>
                        </ol>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="bg-white rounded-xl p-4 text-center shadow-sm">
                <div className="text-2xl font-bold text-slate-900">0:00</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Duration</div>
              </div>
              <div className="bg-white rounded-xl p-4 text-center shadow-sm">
                <div className="text-2xl font-bold text-slate-900">0</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Participants</div>
              </div>
              <div className="bg-white rounded-xl p-4 text-center shadow-sm">
                <div className="text-2xl font-bold text-green-600">Ready</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">Status</div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}

export default Dashboard
